import { datadogLogs, type LogsInitConfiguration } from "@datadog/browser-logs";
import { datadogRum, type RumInitConfiguration } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";

import type { AuthenticationDetails, InitOptions } from "./types";

export const datadogAttachAuth = (details: AuthenticationDetails) => {
  datadogLogs.setUser({
    id: details.user.id,
    email: details.user.email,
    org_id: details.org.id,
  });
  datadogRum.setUser({
    id: details.user.id,
    username: `${details.user.given_name} ${details.user.family_name}`,
    email: details.user.email,
    hasuraOrgId: details.org.id,
    auth0OrgId: details.org.id,
  });
};

export const datadogInit = (p: InitOptions) => {
  const devDatadogClientToken = "pub35e54dd1a81ef0b11eff8104736aa1e7";
  const prodDatadogClientToken = "pubbd9608dace8c0297d5c4b3b5b5cf2bc0";

  const clientToken = p.isProduction ? prodDatadogClientToken : devDatadogClientToken;

  const datadogBaseConfig = {
    applicationId: "bfe234c9-b7f3-4c2a-ba31-3438223e9fe0",
    clientToken,
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: "datadoghq.eu",
    service: p.service,
    env: p.env,
    version: p.version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    plugins: [reactPlugin({ router: false })],
  } as const satisfies RumInitConfiguration;

  const datadogConfig = {
    ...datadogBaseConfig,
    compressIntakeRequests: true,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    trackViewsManually: true,
    storeContextsAcrossPages: true,
    silentMultipleInit: true,
    allowFallbackToLocalStorage: true,
  } satisfies RumInitConfiguration;

  const datadogLogsConfig = {
    ...datadogBaseConfig,
    sessionSampleRate: 100,
    // forwardConsoleLogs: [], // keep console forwarding OFF
    forwardErrorsToLogs: true,
    // debug logs are not forwarded to datadog
    forwardConsoleLogs: ["info", "warn", "error"],
    telemetrySampleRate: 100,
    telemetryConfigurationSampleRate: 100,
    telemetryUsageSampleRate: 100,
  } satisfies LogsInitConfiguration;

  datadogRum.init(datadogConfig);
  datadogLogs.init(datadogLogsConfig);

  if (p.branch) {
    datadogRum.setGlobalContextProperty("git_branch", p.branch);
    datadogLogs.setGlobalContextProperty("git_branch", p.branch);
  }

  if (p.tag) {
    datadogRum.setGlobalContextProperty("git_tag", p.tag);
    datadogLogs.setGlobalContextProperty("git_tag", p.tag);
  }

  return { rum: datadogRum, logs: datadogLogs };
};
